import React, { useState, useEffect, useRef } from 'react';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import Icon from './AppIcon';
import { LEADERSHIP_TEAM } from '../constants/teamData';

const LeadershipManagement = () => {
  const { currentColors } = useAdminTheme();
  const [teamMembers, setTeamMembers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [newMember, setNewMember] = useState({
    name: '',
    title: '',
    description: '',
    location: '',
    linkedin: '',
    image: '',
    type: 'leadership'
  });

  const [editingMember, setEditingMember] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Image editing states
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [editingImageFor, setEditingImageFor] = useState(null); // 'new' or 'edit'
  const [uploadedImage, setUploadedImage] = useState(null);
  const [cropData, setCropData] = useState({ x: 0, y: 0, width: 200, height: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  const memberTypes = [
    { value: 'leadership', label: 'Leadership Team' },
    { value: 'advisory', label: 'Advisory Board' }
  ];

  useEffect(() => {
    loadTeamMembers();
  }, []);

  // Add global mouse event listeners for drag functionality
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (showImageEditor) {
        handleMouseMove(e);
      }
    };

    const handleGlobalMouseUp = () => {
      if (showImageEditor) {
        handleMouseUp();
      }
    };

    if (showImageEditor) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [showImageEditor, isDragging, cropData, dragStart]);

  const loadTeamMembers = () => {
    // Try to load from localStorage first (for persistence across sessions)
    const savedTeamMembers = localStorage.getItem('leadership_team_members');
    if (savedTeamMembers) {
      try {
        const parsedMembers = JSON.parse(savedTeamMembers);
        setTeamMembers(parsedMembers);
        return;
      } catch (error) {
        console.error('Error parsing saved team members:', error);
      }
    }

    // Load from constants if no saved data
    const formattedData = LEADERSHIP_TEAM.map(member => ({
      id: member.id,
      name: member.name,
      title: member.title,
      description: member.description,
      expertise: member.expertise || [],
      location: member.location,
      linkedin: member.linkedin,
      image_url: member.image,
      type: member.title.toLowerCase().includes('advisory') ? 'advisory' : 'leadership',
      created_at: new Date().toISOString()
    }));
    setTeamMembers(formattedData);
  };

  // Save team members to localStorage whenever they change
  const saveTeamMembers = (members) => {
    localStorage.setItem('leadership_team_members', JSON.stringify(members));
  };

  const saveMember = () => {
    if (!newMember.name.trim() || !newMember.title.trim()) {
      alert('Please fill in at least name and title');
      return;
    }

    const newTeamMember = {
      ...newMember,
      id: `temp-${Date.now()}`,
      created_at: new Date().toISOString(),
      expertise: []
    };

    const updatedMembers = [newTeamMember, ...teamMembers];
    setTeamMembers(updatedMembers);
    saveTeamMembers(updatedMembers); // Save to localStorage

    setNewMember({
      name: '', title: '', description: '', location: '',
      linkedin: '', image: '', type: 'leadership'
    });
    setShowCreateModal(false);
    alert('Team member added successfully!');
  };

  const deleteMember = (memberId) => {
    if (window.confirm('Are you sure you want to delete this team member?')) {
      const updatedMembers = teamMembers.filter(member => member.id !== memberId);
      setTeamMembers(updatedMembers);
      saveTeamMembers(updatedMembers); // Save to localStorage
      alert('Team member deleted successfully!');
    }
  };

  const editMember = (member) => {
    setEditingMember({
      ...member,
      image: member.image_url || '',
      expertise: member.expertise || []
    });
    setShowEditModal(true);
  };

  const updateMember = () => {
    if (!editingMember.name.trim() || !editingMember.title.trim()) {
      alert('Please fill in at least name and title');
      return;
    }

    const updatedMembers = teamMembers.map(member =>
      member.id === editingMember.id
        ? { ...editingMember, image_url: editingMember.image }
        : member
    );
    setTeamMembers(updatedMembers);
    saveTeamMembers(updatedMembers); // Save to localStorage

    setEditingMember(null);
    setShowEditModal(false);
    alert('Team member updated successfully!');
  };

  // Image editing functions
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      alert('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(e.target.result);
      setShowImageEditor(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropImage = () => {
    if (!uploadedImage || !canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    // Set canvas size to crop dimensions
    canvas.width = cropData.width;
    canvas.height = cropData.height;

    // Draw cropped image
    ctx.drawImage(
      img,
      cropData.x,
      cropData.y,
      cropData.width,
      cropData.height,
      0,
      0,
      cropData.width,
      cropData.height
    );

    // Convert to data URL
    const croppedImageUrl = canvas.toDataURL('image/jpeg', 0.9);

    // Apply the cropped image
    if (editingImageFor === 'new') {
      setNewMember(prev => ({ ...prev, image: croppedImageUrl }));
    } else if (editingImageFor === 'edit') {
      setEditingMember(prev => ({ ...prev, image: croppedImageUrl }));
    }

    // Reset states
    setShowImageEditor(false);
    setUploadedImage(null);
    setCropData({ x: 0, y: 0, width: 200, height: 200 });
    alert('Image cropped and applied successfully!');
  };

  const openImageEditor = (forType) => {
    setEditingImageFor(forType);
    // Trigger file input based on context
    const inputId = forType === 'edit' ? 'edit-profile-image-upload' : 'profile-image-upload';
    const fileInput = document.getElementById(inputId);
    if (fileInput) {
      fileInput.click();
    }
  };

  // Drag functionality for crop area
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - cropData.x,
      y: e.clientY - cropData.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !imageRef.current) return;

    const img = imageRef.current;
    const rect = img.getBoundingClientRect();

    const newX = Math.max(0, Math.min(e.clientX - dragStart.x, rect.width - cropData.width));
    const newY = Math.max(0, Math.min(e.clientY - dragStart.y, rect.height - cropData.height));

    setCropData(prev => ({
      ...prev,
      x: newX,
      y: newY
    }));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Reset drag states when canceling
  const cancelImageEdit = () => {
    setShowImageEditor(false);
    setUploadedImage(null);
    setCropData({ x: 0, y: 0, width: 200, height: 200 });
    setIsDragging(false);
    setDragStart({ x: 0, y: 0 });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Icon name="Loader2" size={24} className="animate-spin" style={{ color: currentColors.primary }} />
        <span className="ml-2" style={{ color: currentColors.text }}>Loading team members...</span>
      </div>
    );
  }

  return (
    <div className="p-6" style={{ backgroundColor: currentColors.background }}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: currentColors.text }}>
            Leadership & Advisory Team Management
          </h2>
          <p style={{ color: currentColors.textMuted }}>
            Manage leadership team and advisory board members
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
          style={{
            backgroundColor: currentColors.primary,
            color: '#ffffff'
          }}
        >
          <Icon name="Plus" size={16} />
          Add Team Member
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="p-4 rounded-lg border" style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentColors.textMuted }}>Total Members</p>
              <p className="text-2xl font-bold" style={{ color: currentColors.text }}>{teamMembers.length}</p>
            </div>
            <Icon name="Users" size={24} style={{ color: currentColors.primary }} />
          </div>
        </div>
        <div className="p-4 rounded-lg border" style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentColors.textMuted }}>Leadership Team</p>
              <p className="text-2xl font-bold" style={{ color: currentColors.text }}>
                {teamMembers.filter(m => m.type === 'leadership').length}
              </p>
            </div>
            <Icon name="Crown" size={24} style={{ color: currentColors.primary }} />
          </div>
        </div>
        <div className="p-4 rounded-lg border" style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentColors.textMuted }}>Advisory Board</p>
              <p className="text-2xl font-bold" style={{ color: currentColors.text }}>
                {teamMembers.filter(m => m.type === 'advisory').length}
              </p>
            </div>
            <Icon name="Star" size={24} style={{ color: currentColors.primary }} />
          </div>
        </div>
      </div>

      {/* Team Members Table */}
      <div className="rounded-lg border overflow-hidden" style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}>
        <table className="w-full">
          <thead>
            <tr className="border-b" style={{ borderColor: currentColors.border }}>
              <th className="text-left p-4" style={{ color: currentColors.text }}>Member</th>
              <th className="text-left p-4" style={{ color: currentColors.text }}>Title</th>
              <th className="text-left p-4" style={{ color: currentColors.text }}>Type</th>
              <th className="text-left p-4" style={{ color: currentColors.text }}>Location</th>
              <th className="text-left p-4" style={{ color: currentColors.text }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {teamMembers.map((member, index) => (
              <tr key={member.id || index} className="border-b hover:bg-opacity-50 hover:bg-muted" style={{ borderColor: currentColors.border }}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img
                      src={member.image_url || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'}
                      alt={member.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium" style={{ color: currentColors.text }}>{member.name}</p>
                      {member.linkedin && (
                        <a
                          href={member.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm flex items-center gap-1 mt-1"
                          style={{ color: currentColors.primary }}
                        >
                          <Icon name="Linkedin" size={12} />
                          LinkedIn
                        </a>
                      )}
                    </div>
                  </div>
                </td>
                <td className="p-4" style={{ color: currentColors.text }}>{member.title}</td>
                <td className="p-4">
                  <span
                    className="px-2 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${currentColors.primary}20`,
                      color: currentColors.primary
                    }}
                  >
                    {memberTypes.find(t => t.value === member.type)?.label || member.type}
                  </span>
                </td>
                <td className="p-4" style={{ color: currentColors.text }}>{member.location}</td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedMember(member);
                        setShowDetailModal(true);
                      }}
                      className="p-2 rounded-lg transition-all duration-200"
                      style={{
                        backgroundColor: `${currentColors.primary}20`,
                        color: currentColors.primary
                      }}
                      title="View Details"
                    >
                      <Icon name="Eye" size={16} />
                    </button>
                    <button
                      onClick={() => editMember(member)}
                      className="p-2 rounded-lg transition-all duration-200"
                      style={{
                        backgroundColor: '#fef3c7',
                        color: '#d97706'
                      }}
                      title="Edit Member"
                    >
                      <Icon name="Edit" size={16} />
                    </button>
                    <button
                      onClick={() => deleteMember(member.id)}
                      className="p-2 rounded-lg transition-all duration-200"
                      style={{
                        backgroundColor: '#fee2e2',
                        color: '#dc2626'
                      }}
                      title="Delete Member"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className="rounded-lg p-6 w-full max-w-lg"
            style={{ backgroundColor: currentColors.surface }}
          >
            <h3 className="text-xl font-bold mb-4" style={{ color: currentColors.text }}>
              Add Team Member
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={newMember.name}
                  onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: currentColors.background,
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={newMember.title}
                  onChange={(e) => setNewMember({...newMember, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: currentColors.background,
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                  placeholder="e.g., CEO, Director, Advisor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Type
                </label>
                <select
                  value={newMember.type}
                  onChange={(e) => setNewMember({...newMember, type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: currentColors.background,
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                >
                  {memberTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Location
                </label>
                <input
                  type="text"
                  value={newMember.location}
                  onChange={(e) => setNewMember({...newMember, location: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: currentColors.background,
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                  placeholder="e.g., Bengaluru, Karnataka"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  LinkedIn URL
                </label>
                <input
                  type="url"
                  value={newMember.linkedin}
                  onChange={(e) => setNewMember({...newMember, linkedin: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: currentColors.background,
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                  placeholder="https://linkedin.com/in/username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Profile Image
                </label>
                <div className="space-y-3">
                  {/* Hidden file input */}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="profile-image-upload"
                    onChange={handleImageUpload}
                  />

                  {/* Upload button */}
                  <button
                    type="button"
                    onClick={() => openImageEditor('new')}
                    className="w-full px-4 py-3 border-2 border-dashed rounded-lg transition-all duration-200 hover:border-opacity-70"
                    style={{
                      borderColor: currentColors.primary,
                      backgroundColor: `${currentColors.primary}10`
                    }}
                  >
                    <div className="flex items-center justify-center gap-3">
                      <Icon name="Upload" size={20} style={{ color: currentColors.primary }} />
                      <span style={{ color: currentColors.primary }}>
                        Upload & Edit Image
                      </span>
                    </div>
                  </button>

                  {/* Alternative URL input */}
                  <div className="text-center text-sm" style={{ color: currentColors.textMuted }}>
                    Or enter image URL directly:
                  </div>
                  <input
                    type="url"
                    value={newMember.image}
                    onChange={(e) => setNewMember({...newMember, image: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                    placeholder="https://example.com/image.jpg"
                  />

                  {/* Image preview */}
                  {newMember.image && (
                    <div className="flex justify-center">
                      <img
                        src={newMember.image}
                        alt="Preview"
                        className="w-20 h-20 rounded-full object-cover border-2"
                        style={{ borderColor: currentColors.border }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Description
                </label>
                <textarea
                  value={newMember.description}
                  onChange={(e) => setNewMember({...newMember, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: currentColors.background,
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                  placeholder="Brief description of the team member..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                style={{
                  backgroundColor: currentColors.border,
                  color: currentColors.text
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveMember}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                style={{
                  backgroundColor: currentColors.primary,
                  color: '#ffffff'
                }}
              >
                Save Member
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className="rounded-lg w-full max-w-lg max-h-[90vh] flex flex-col"
            style={{ backgroundColor: currentColors.surface }}
          >
            <div className="p-6 border-b" style={{ borderColor: currentColors.border }}>
              <h3 className="text-xl font-bold" style={{ color: currentColors.text }}>
                Edit Team Member
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={editingMember.name}
                  onChange={(e) => setEditingMember({...editingMember, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: currentColors.background,
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={editingMember.title}
                  onChange={(e) => setEditingMember({...editingMember, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: currentColors.background,
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Type
                </label>
                <select
                  value={editingMember.type}
                  onChange={(e) => setEditingMember({...editingMember, type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: currentColors.background,
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                >
                  {memberTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Location
                </label>
                <input
                  type="text"
                  value={editingMember.location}
                  onChange={(e) => setEditingMember({...editingMember, location: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: currentColors.background,
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  LinkedIn URL
                </label>
                <input
                  type="url"
                  value={editingMember.linkedin}
                  onChange={(e) => setEditingMember({...editingMember, linkedin: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: currentColors.background,
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Profile Image
                </label>
                <div className="space-y-3">
                  {/* Hidden file input for edit */}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="edit-profile-image-upload"
                    onChange={handleImageUpload}
                  />

                  {/* Upload button */}
                  <button
                    type="button"
                    onClick={() => openImageEditor('edit')}
                    className="w-full px-4 py-3 border-2 border-dashed rounded-lg transition-all duration-200 hover:border-opacity-70"
                    style={{
                      borderColor: currentColors.primary,
                      backgroundColor: `${currentColors.primary}10`
                    }}
                  >
                    <div className="flex items-center justify-center gap-3">
                      <Icon name="Upload" size={20} style={{ color: currentColors.primary }} />
                      <span style={{ color: currentColors.primary }}>
                        Upload & Edit Image
                      </span>
                    </div>
                  </button>

                  {/* Alternative URL input */}
                  <div className="text-center text-sm" style={{ color: currentColors.textMuted }}>
                    Or enter image URL directly:
                  </div>
                  <input
                    type="url"
                    value={editingMember.image}
                    onChange={(e) => setEditingMember({...editingMember, image: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                    placeholder="https://example.com/image.jpg"
                  />

                  {/* Image preview */}
                  {editingMember.image && (
                    <div className="flex justify-center">
                      <img
                        src={editingMember.image}
                        alt="Preview"
                        className="w-20 h-20 rounded-full object-cover border-2"
                        style={{ borderColor: currentColors.border }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Description
                </label>
                <textarea
                  value={editingMember.description}
                  onChange={(e) => setEditingMember({...editingMember, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: currentColors.background,
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                />
              </div>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3" style={{ borderColor: currentColors.border }}>
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                style={{
                  backgroundColor: currentColors.border,
                  color: currentColors.text
                }}
              >
                Cancel
              </button>
              <button
                onClick={updateMember}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                style={{
                  backgroundColor: currentColors.primary,
                  color: '#ffffff'
                }}
              >
                Update Member
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Editor Modal */}
      {showImageEditor && uploadedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div
            className="rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: currentColors.surface }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ color: currentColors.text }}>
                Edit Profile Image
              </h3>
              <button
                onClick={cancelImageEdit}
                className="p-2 rounded-lg transition-all duration-200"
                style={{ color: currentColors.textMuted }}
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Image Preview with Crop Area */}
              <div className="flex justify-center">
                <div className="relative">
                  <img
                    ref={imageRef}
                    src={uploadedImage}
                    alt="Upload preview"
                    className="max-w-full max-h-96 object-contain rounded-lg"
                    onLoad={(e) => {
                      // Auto-center crop area when image loads
                      const img = e.target;
                      const size = Math.min(img.offsetWidth, img.offsetHeight, 200);
                      setCropData({
                        x: (img.offsetWidth - size) / 2,
                        y: (img.offsetHeight - size) / 2,
                        width: size,
                        height: size
                      });
                    }}
                  />

                  {/* Crop overlay */}
                  <div
                    className="absolute border-2 border-white border-opacity-80 shadow-lg cursor-move"
                    style={{
                      left: cropData.x,
                      top: cropData.y,
                      width: cropData.width,
                      height: cropData.height,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  >
                    {/* Corner handles */}
                    <div className="absolute -top-1 -left-1 w-3 h-3 bg-card rounded-full border border-border cursor-nw-resize"></div>
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-card rounded-full border border-border cursor-ne-resize"></div>
                    <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-card rounded-full border border-border cursor-sw-resize"></div>
                    <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-card rounded-full border border-border cursor-se-resize"></div>

                    {/* Drag hint */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                        Drag to reposition
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Crop Controls */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Crop Width: {cropData.width}px
                  </label>
                  <input
                    type="range"
                    min="50"
                    max="400"
                    value={cropData.width}
                    onChange={(e) => setCropData(prev => ({ ...prev, width: parseInt(e.target.value) }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Crop Height: {cropData.height}px
                  </label>
                  <input
                    type="range"
                    min="50"
                    max="400"
                    value={cropData.height}
                    onChange={(e) => setCropData(prev => ({ ...prev, height: parseInt(e.target.value) }))}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Hidden canvas for processing */}
              <canvas ref={canvasRef} className="hidden" />

              {/* Action buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={cancelImageEdit}
                  className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                  style={{
                    backgroundColor: currentColors.border,
                    color: currentColors.text
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCropImage}
                  className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                  style={{
                    backgroundColor: currentColors.primary,
                    color: '#ffffff'
                  }}
                >
                  Apply Crop
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div 
            className="rounded-lg p-6 w-full max-w-lg"
            style={{ backgroundColor: currentColors.surface }}
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold" style={{ color: currentColors.text }}>
                {selectedMember.name}
              </h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 rounded-lg transition-all duration-200"
                style={{ color: currentColors.textMuted }}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            
            <div className="space-y-4">
              <img
                src={selectedMember.image_url || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'}
                alt={selectedMember.name}
                className="w-20 h-20 rounded-full object-cover"
              />
              
              <div>
                <p className="font-medium" style={{ color: currentColors.text }}>{selectedMember.title}</p>
                <p className="text-sm" style={{ color: currentColors.textMuted }}>{selectedMember.location}</p>
              </div>
              
              <p style={{ color: currentColors.text }}>{selectedMember.description}</p>
              
              {selectedMember.expertise && selectedMember.expertise.length > 0 && (
                <div>
                  <p className="font-medium mb-2" style={{ color: currentColors.text }}>Expertise:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedMember.expertise.map((skill, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 rounded-full text-xs"
                        style={{
                          backgroundColor: `${currentColors.primary}20`,
                          color: currentColors.primary
                        }}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedMember.linkedin && (
                <a
                  href={selectedMember.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200"
                  style={{
                    backgroundColor: `${currentColors.primary}20`,
                    color: currentColors.primary
                  }}
                >
                  <Icon name="Linkedin" size={16} />
                  View LinkedIn Profile
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadershipManagement;
